import {
  ClipboardListIcon,
  FileMinusIcon,
  FileTextIcon,
  HashIcon,
  ListChecksIcon,
  PackageCheckIcon,
  PlusIcon,
  ReceiptTextIcon,
  SearchIcon,
  SettingsIcon,
  TruckIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react"
import type * as React from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import type { Capability } from "@steelbill/types"
import {
  Command,
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import { useAuth } from "@/features/auth/auth-context"
import { usePreferencesSheet } from "@/features/preferences/preferences-sheet"
import {
  MIN_SEARCH_LENGTH,
  useGlobalSearch,
  type SearchHit,
} from "@/features/search/use-global-search"
import { SUPPORTED_LOCALES } from "@/i18n"
import { HOME_SCREEN_CAPABILITIES } from "@/routes/landing"
import { routes } from "@/routes/paths"

/**
 * Search everything, from anywhere (§11.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE BOX FOR FIVE REGISTERS PLUS NAVIGATION. Typing "Lupin" finds the customer;
 * "26-27" finds bills; "CHL" finds delivery challans; "shutter" finds saved
 * jobs; "cust" jumps to the Customers screen. The owner does not have to know
 * which register a thing lives in before he can look for it, which is the entire
 * point — and that extends to the screens themselves: "unpaid", "number",
 * "setting" all land somewhere, so the palette answers "where is that?" as well
 * as "where is this document?".
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * TWO KINDS OF ROW, AND ONLY ONE OF THEM IS FILTERED BY WHAT YOU TYPE.
 *
 * cmdk's OWN filter is disabled (`shouldFilter={false}`) and must stay disabled.
 * It matches against the RENDERED LABEL, and the server rows have already been
 * matched — against fields the row does not display. A bill found by its S.O.
 * number would vanish, because the row shows the invoice number instead. Leaving
 * it on drops exactly the results that made searching worthwhile, and it looks
 * like "nothing matched" rather than like a bug.
 *
 * But the STATIC rows — "Go to" and "Create" — are not server answers, and for a
 * while nothing filtered them at all: every entry the user could reach was drawn
 * on every keystroke. Typing "report" did not MATCH Reports; Reports was simply
 * always there, which is why the owner experienced the screens as missing from
 * search. So they are filtered HERE, by {@link labelMatches}, against the
 * DEBOUNCED query — never by re-enabling cmdk's filter, which cannot tell the
 * two kinds of row apart.
 *
 * The match is on the TRANSLATED LABEL, in BOTH languages, case- and
 * accent-insensitively: he reads the app in Marathi (§11.4) and may still type
 * "bill" on a Latin keyboard, so an entry answers to what it says in either
 * script. An EMPTY box shows them all — a palette with nothing typed in it is a
 * menu, and that is the right thing for a freshly opened box to be.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EVERY ENTRY IS CAPABILITY-GATED (§2.1), the navigation ones and the result
 * rows alike, using the same `can()` the sidebar and the server use. A search
 * result that 403s when opened is worse than one that never appears. Note that
 * the three settings destinations sit behind THREE DIFFERENT capabilities and
 * must not be collapsed into one: `settings.write` is ADMIN-only, the counter
 * override is `invoice.set_counter` (§6.1 — folding it into `settings.write`
 * once made it unreachable for the one person who needs it), and a person's own
 * preferences are gated by nothing at all, deliberately.
 *
 * A ROW THAT OPENS IS A ROW THAT ARRIVES. The gate is only half of that promise;
 * the destination has to exist. Two rows failed the second half while passing
 * the first, and both looked identical to the owner — the palette closed and the
 * dashboard appeared. "My settings" pointed at a `/profile` path no route table
 * ever mounted (it is a sheet, and opens as one now), and "Bill and quotation
 * numbers" pointed at a route that redirected into the `settings.write`-gated
 * Settings screen. A capability check cannot catch either; only following the
 * row can, which is what `capability-reachability.test.tsx` does.
 *
 * FEATURE FLAGS ARE NOT CHECKED HERE, because there is nothing to check them
 * with: there is no `useFlag` on the client and this file does not invent one
 * (§18.3, and see `use-global-search.ts`). A flagged-off register answers 404 to
 * the search request and contributes no rows; a flagged-off DESTINATION is
 * gated on its capability alone and lands on a screen that says plainly it is
 * not switched on.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Compare-ready text: lower case, and blind to the accents a hurried typist
 * drops. NFD splits a letter from its accent so the combining mark can be
 * removed; Devanagari vowel signs are not `Diacritic`, so Marathi labels survive
 * this unchanged and only the nukta is folded away.
 */
function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
}

/**
 * A row that is always here — a screen to jump to, or something to start.
 *
 * `capability: null` means "no gate", which exactly one entry uses and which is
 * not an oversight — see the preferences row below.
 *
 * `select` RATHER THAN A PATH, because not every destination is a path. The
 * signed-in person's own preferences are a SHEET opened in place, deliberately
 * (`ProfileSheet.tsx`), and modelling every row as a URL is what put a
 * `routes.profile` here that no route table ever mounted: selecting it fell
 * through the catch-all and landed on the dashboard, silently. `key` is what
 * React and cmdk identify the row by — the path for a navigation, a name for the
 * one row that is not one.
 */
interface StaticEntry {
  titleKey: string
  key: string
  /**
   * What the reader must hold for this row to be offered.
   *
   * A LIST MEANS "ANY ONE OF THESE", and — exactly as in `app-sidebar.tsx`'s
   * `NavItem` — precisely one row needs it: HOME. Every other destination is one
   * screen behind one capability, and a list there would be a quiet way of
   * widening a gate, so the plain form stays and is what everything else uses.
   */
  capability: Capability | readonly Capability[] | null
  icon: React.ReactNode
  select: () => void
}

interface GlobalSearchContextValue {
  open: () => void
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null)

/** Lets the header button (or anything else) open the palette. */
export function useGlobalSearchDialog(): GlobalSearchContextValue {
  const context = useContext(GlobalSearchContext)
  // A no-op rather than a throw: a component rendered outside the provider in a
  // test should not blow up over a search box it never uses.
  return context ?? { open: () => undefined }
}

export function GlobalSearchProvider({
  children,
}: {
  children: React.ReactNode
}): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const results = useGlobalSearch(query)

  /*
   * ⌘K on a Mac, Ctrl+K elsewhere — the shortcut people already try. `/` is
   * deliberately NOT bound: this app has text inputs everywhere and stealing a
   * printable character would make the invoice description field unusable.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((current) => !current)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  // A stale query when the palette reopens would show last time's results for
  // an empty box.
  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const go = useCallback(
    (to: string) => {
      setOpen(false)
      void navigate(to)
    },
    [navigate],
  )

  /** The one row that opens something in place rather than navigating. */
  const preferences = usePreferencesSheet()

  /** Spread into a static entry whose destination IS a path — most of them. */
  const goTo = (to: string) => ({
    key: to,
    select: () => {
      go(to)
    },
  })

  const value = useMemo<GlobalSearchContextValue>(
    () => ({
      open: () => {
        setOpen(true)
      },
    }),
    [],
  )

  /** Screens worth jumping to, each gated like its sidebar item. */
  const destinations: StaticEntry[] = [
    /*
     * ═══════════════════════════════════════════════════════════════════════
     * HOME IS GATED ON `HOME_SCREEN_CAPABILITIES`, THE SAME LIST THE RAIL AND
     * THE `/` ROUTE USE — NOT ON `report.view`.
     *
     * `report.view` was always the wrong question here, and it became a visible
     * defect the day the ACCOUNTANT could sign in. She holds it, so the row was
     * offered to her; selecting it navigated to `/`, where `LandingRoute`
     * resolves her landing to `/reports` and sent her straight back to the
     * screen she was already on. The palette closed and nothing happened —
     * indistinguishable from the app ignoring the tap, on the row she was most
     * likely to try first.
     *
     * THE FIX LANDED IN THE SIDEBAR AND NOT HERE, which is the actual lesson.
     * There are two hand-kept lists of destinations in this app — `app-sidebar`'s
     * `NavItem[]` and this array — and a rule changed in one of them is a rule
     * that has not changed. Both now read `HOME_SCREEN_CAPABILITIES` from
     * `routes/landing.ts`, which is also what `/` resolves with, so a row that
     * says "Home" arrives at Home for everybody it is offered to.
     * ═══════════════════════════════════════════════════════════════════════
     */
    {
      titleKey: "nav.home",
      ...goTo(routes.home),
      capability: HOME_SCREEN_CAPABILITIES,
      icon: <SearchIcon />,
    },
    {
      titleKey: "nav.invoices",
      ...goTo(routes.invoices),
      capability: "invoice.view",
      icon: <ReceiptTextIcon />,
    },
    /*
     * §17.5's register — the bills with something still owed on them. Gated on
     * `payment.view`, which is a DIFFERENT capability from `invoice.view` on
     * purpose: it is a money question, and the CA holds it while holding no
     * invoice write at all.
     *
     * The `payments` flag is not consulted, because the client has no way to
     * consult one (§18.3 — see the header). With the module off this lands on
     * the bill register with its unpaid filter inert, which is a harmless
     * screen rather than an error.
     */
    {
      titleKey: "home.card.unpaidBills",
      ...goTo(routes.invoicesUnpaid),
      capability: "payment.view",
      icon: <WalletIcon />,
    },
    {
      titleKey: "nav.quotations",
      ...goTo(routes.quotations),
      capability: "quotation.view",
      icon: <FileTextIcon />,
    },
    {
      titleKey: "nav.challans",
      ...goTo(routes.challans),
      capability: "challan.view",
      icon: <TruckIcon />,
    },
    /*
     * §17.7's payoff — the deliveries still waiting for a bill, and the one
     * challan screen the owner has a reason to open cold rather than from a
     * document. Same capability as the register it filters.
     */
    {
      titleKey: "list.onlyUnbilled",
      ...goTo(routes.challansUnbilled),
      capability: "challan.view",
      icon: <PackageCheckIcon />,
    },
    /*
     * §17.3's register. A READING destination and nothing more — there is no
     * "New credit note" entry in the `creations` list below, deliberately: a
     * credit note is raised from the bill it corrects (rule 1), so the only
     * control that starts one lives on an invoice. A "Create" row here would be
     * a second way in, with the invoice unanswered.
     */
    {
      titleKey: "nav.creditNotes",
      ...goTo(routes.creditNotes),
      capability: "creditnote.view",
      icon: <FileMinusIcon />,
    },
    {
      titleKey: "nav.customers",
      ...goTo(routes.customers),
      capability: "customer.view",
      icon: <UsersIcon />,
    },
    {
      titleKey: "nav.items",
      ...goTo(routes.items),
      capability: "item.view",
      icon: <ListChecksIcon />,
    },
    {
      titleKey: "nav.reports",
      ...goTo(routes.reports),
      capability: "report.view",
      icon: <ClipboardListIcon />,
    },
    /* The company's own details, the tax master, the bank — ADMIN only (§2.1). */
    {
      titleKey: "nav.settings",
      ...goTo(routes.settings),
      capability: "settings.write",
      icon: <SettingsIcon />,
    },
    /*
     * §6.1's counter override, on `invoice.set_counter` and NOT on
     * `settings.write`. The two look alike from here and are not: the OWNER
     * holds this one and not that one, because he is the person who writes bills
     * by hand and has to nudge the series back into line afterwards. Collapsing
     * the two gates once put this screen behind a door he cannot open — and for
     * a while the ROUTE did it again from the other end, redirecting
     * `/invoices/numbers` to the `settings.write`-gated Settings screen. Both
     * halves have to agree: `routes/index.tsx` mounts this on
     * `invoice.set_counter` and nothing else.
     */
    {
      titleKey: "counters.title",
      ...goTo(routes.invoiceNumbers),
      capability: "invoice.set_counter",
      icon: <HashIcon />,
    },
    /*
     * UNGATED, DELIBERATELY. Language and text size belong to whoever is holding
     * the phone, and the owner — who holds no `settings.write` — is precisely
     * the person who needs bigger type and a Marathi interface (§11.4). Putting
     * a capability here would be the same mistake, one screen further out.
     *
     * AND IT IS NOT A NAVIGATION. This row used to point at `routes.profile`,
     * "/profile" — a path `routes/index.tsx` has never mounted, because the
     * preferences are a SHEET opened in place and always have been. Selecting it
     * matched no route, fell through the catch-all and landed on the dashboard:
     * the palette closed, no sheet opened, nothing explained it. The single row
     * with no capability gate — the one every signed-in person can see — was the
     * only broken one in the list.
     */
    {
      titleKey: "pref.title",
      key: "preferences",
      capability: null,
      icon: <UserIcon />,
      select: () => {
        setOpen(false)
        preferences.open()
      },
    },
  ]

  const creations: StaticEntry[] = [
    {
      titleKey: "btn.newInvoice",
      ...goTo(routes.invoiceNew),
      capability: "invoice.create",
      icon: <PlusIcon />,
    },
    {
      titleKey: "btn.newQuotation",
      ...goTo(routes.quotationNew),
      capability: "quotation.create",
      icon: <PlusIcon />,
    },
    {
      titleKey: "btn.newChallan",
      ...goTo(routes.challanNew),
      capability: "challan.create",
      icon: <PlusIcon />,
    },
    {
      titleKey: "btn.addNewCustomer",
      ...goTo(routes.customerNew),
      capability: "customer.create",
      icon: <PlusIcon />,
    },
    {
      titleKey: "btn.addNewItem",
      ...goTo(routes.itemNew),
      capability: "item.create",
      icon: <PlusIcon />,
    },
  ]

  /*
   * WHAT THE STATIC ROWS ARE FILTERED AGAINST. The DEBOUNCED query, so the
   * navigation entries move in step with the rows above them rather than a
   * quarter-second ahead — except when the box has just been EMPTIED, which
   * restores the full menu at once. The debounce exists to spare the network and
   * there is no request behind an empty query, so making him watch the menu
   * reappear would be a delay bought for nothing.
   */
  const needle = query.trim() === "" ? "" : fold(results.debouncedQuery.trim())

  /**
   * Does this entry answer to what has been typed?
   *
   * Both languages, because the label he reads is Marathi and the letters he
   * types are as likely to be Latin (§11.4). An empty needle matches everything:
   * see the header — an untouched palette is a menu.
   */
  const labelMatches = (titleKey: string): boolean =>
    needle === "" ||
    SUPPORTED_LOCALES.some((locale) => fold(i18n.t(titleKey, { lng: locale })).includes(needle))

  /**
   * Does the reader hold this row's gate? A LIST IS "ANY ONE OF THESE" —
   * deliberately the same resolution `app-sidebar.tsx` applies to `NavItem`, so
   * the two lists of destinations answer the same question the same way.
   */
  const holds = (capability: Capability | readonly Capability[] | null): boolean =>
    capability === null ||
    (typeof capability === "string" ? can(capability) : capability.some((c) => can(c)))

  const allowed = (entry: StaticEntry): boolean =>
    holds(entry.capability) && labelMatches(entry.titleKey)

  const visibleDestinations = destinations.filter(allowed)
  const visibleCreations = creations.filter(allowed)

  const typed = query.trim().length
  const typing = typed >= MIN_SEARCH_LENGTH
  const hits =
    results.invoices.length +
    results.quotations.length +
    results.challans.length +
    results.creditNotes.length +
    results.customers.length +
    results.items.length

  /*
   * WHY THIS IS NOT `CommandEmpty`. cmdk renders that component only when it
   * counts ZERO items in the list — and this list always carries the "Go to"
   * and "Create" groups, so the count is never zero and `CommandEmpty` never
   * appears. Written that way it typechecked, rendered, and silently said
   * nothing: an unrecognised name looked like a search still in progress.
   *
   * Three different facts, told apart, because conflating them is what makes a
   * search box feel broken:
   *   - one character typed  → the app is not searching yet, and why
   *   - searching            → an answer is coming, do not retype
   *   - searched, found none → that name is genuinely not in the book
   * An empty box says nothing at all: the navigation list below IS the answer
   * to "what can I do from here", and a hint over it would be noise.
   *
   * "FOUND NONE" COUNTS THE STATIC MATCHES TOO, now that they are filtered. A
   * palette that says "Nothing matches that." above a Reports row that plainly
   * DOES match is arguing with itself, and the sentence is the part the reader
   * believes.
   */
  const status =
    typed === 0
      ? null
      : !typing
        ? t("search.keepTyping")
        : results.searching
          ? t("msg.loading")
          : hits === 0 && visibleDestinations.length === 0 && visibleCreations.length === 0
            ? t("search.empty")
            : null

  /**
   * One register's worth of hits.
   *
   * THE CAPABILITY IS CHECKED HERE TOO, not only on the navigation entries. The
   * server refuses a register the caller may not read, so in practice these
   * arrive empty — but §2.1's rule is that the client and the server resolve the
   * same `can()`, and a row this palette would draw is a row it promises to open.
   */
  const section = (
    capability: Capability,
    heading: string,
    rows: SearchHit[],
    icon: React.ReactNode,
    to: (hit: SearchHit) => string,
  ) =>
    rows.length === 0 || !can(capability) ? null : (
      <CommandGroup heading={heading}>
        {rows.map((hit) => (
          <CommandItem
            key={`${hit.kind}-${String(hit.id)}`}
            // `value` must be unique, or cmdk treats two rows with the same
            // label as the same item and only one is selectable.
            value={`${hit.kind}-${String(hit.id)}-${hit.title}`}
            onSelect={() => {
              go(to(hit))
            }}
          >
            {icon}
            <span className="font-medium">{hit.title}</span>
            {hit.subtitle === "" ? null : (
              <span className="text-muted-foreground ml-2 truncate text-xs">{hit.subtitle}</span>
            )}
          </CommandItem>
        ))}
      </CommandGroup>
    )

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t("search.title")}
        description={t("search.help")}
      >
        {/*
          * THE `Command` ROOT IS SUPPLIED HERE, not by `CommandDialog`. This
          * project's `CommandDialog` renders `{children}` straight into the
          * dialog without wrapping them — unlike most shadcn examples, where it
          * provides the root itself. Omitting it leaves `CommandInput` and
          * `CommandList` with no cmdk context and nothing works.
          *
          * `shouldFilter={false}` belongs on this element, and it matters: cmdk
          * would otherwise filter the rendered rows against the typed text a
          * SECOND time. The results already came back from a server search, so
          * a bill matched by its S.O. number would then vanish because the row
          * displays the invoice number instead.
          *
          * DO NOT TURN THIS BACK ON to make "Go to" and "Create" respond to what
          * is typed — they already do, filtered by `labelMatches` above, because
          * cmdk's filter is wholesale and cannot tell a static entry from a
          * server hit. The test "keeps a hit whose visible label does not
          * contain the query" fails the moment this becomes `true`.
          */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("search.placeholder")}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {status === null ? null : (
              <div
                role="status"
                aria-live="polite"
                className="text-muted-foreground px-3 py-4 text-center text-sm"
              >
                {status}
              </div>
            )}

            {section("invoice.view", t("nav.invoices"), results.invoices, <ReceiptTextIcon />, (hit) =>
              routes.invoice(hit.id),
            )}
            {section(
              "quotation.view",
              t("nav.quotations"),
              results.quotations,
              <FileTextIcon />,
              (hit) => routes.quotation(hit.id),
            )}
            {section("challan.view", t("nav.challans"), results.challans, <TruckIcon />, (hit) =>
              routes.challan(hit.id),
            )}
            {/*
              * §17.3 — the fourth document section, directly under the three
              * that issue money and above the two that are not documents at all.
              *
              * `creditnote.view`, the shared read row: the ACCOUNTANT holds it
              * because she files the CDNR section of GSTR-1. The `credit_notes`
              * FLAG needs nothing here — the request 404s while the module is
              * off, `use-global-search.ts` swallows it per-request, and this
              * section is simply not drawn (§18.3).
              */}
            {section(
              "creditnote.view",
              t("nav.creditNotes"),
              results.creditNotes,
              <FileMinusIcon />,
              (hit) => routes.creditNote(hit.id),
            )}
            {section("customer.view", t("nav.customers"), results.customers, <UsersIcon />, (hit) =>
              routes.customer(hit.id),
            )}
            {section("item.view", t("nav.items"), results.items, <ListChecksIcon />, (hit) =>
              routes.item(hit.id),
            )}

            {/*
              * The rule between the two halves — only when there is something on
              * BOTH sides of it. A separator under an empty list is a line
              * across the top of the palette, and one over an empty menu is a
              * line across the bottom.
              */}
            {hits > 0 && visibleDestinations.length + visibleCreations.length > 0 ? (
              <CommandSeparator />
            ) : null}

            {/*
              * These two groups are FILTERED IN OUR OWN CODE (see the header) —
              * `visibleDestinations` and `visibleCreations` are already narrowed
              * by capability AND by what has been typed. A group with nothing
              * left in it is not rendered, or its heading would sit alone.
              */}
            {visibleDestinations.length === 0 ? null : (
              <CommandGroup heading={t("search.groupGoTo")}>
                {visibleDestinations.map((destination) => (
                  <CommandItem
                    key={destination.key}
                    value={`goto-${destination.key}-${t(destination.titleKey)}`}
                    onSelect={destination.select}
                  >
                    {destination.icon}
                    {t(destination.titleKey)}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {visibleCreations.length === 0 ? null : (
              <CommandGroup heading={t("search.groupActions")}>
                {visibleCreations.map((creation) => (
                  <CommandItem
                    key={creation.key}
                    value={`new-${creation.key}-${t(creation.titleKey)}`}
                    onSelect={creation.select}
                  >
                    {creation.icon}
                    {t(creation.titleKey)}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </GlobalSearchContext.Provider>
  )
}

/**
 * The affordance that opens the palette.
 *
 * The `render` prop mirrors {@link ScanAction}'s, and for the same reason: these
 * two live SIDE BY SIDE in the sidebar and have to look like one pair of tools,
 * which they cannot do if one of them ships its own styling. The caller supplies
 * the trigger; this component owns only what opening means.
 */
export function GlobalSearchButton({
  render,
}: {
  /** Supply a custom trigger — the sidebar passes its own menu button. */
  render?: (open: () => void) => React.ReactNode
} = {}): React.JSX.Element {
  const { t } = useTranslation()
  const { open } = useGlobalSearchDialog()

  if (render !== undefined) return <>{render(open)}</>

  return (
    <button
      type="button"
      onClick={open}
      className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm transition-colors sm:min-w-56"
    >
      <SearchIcon aria-hidden="true" className="size-4 shrink-0" />
      <span className="hidden sm:inline">{t("search.placeholder")}</span>
      <span className="sr-only sm:hidden">{t("search.title")}</span>
      <CommandShortcut className="ml-auto hidden sm:inline">⌘K</CommandShortcut>
    </button>
  )
}
